import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { ThemeContext } from 'src/store/ContextStore';
import { FIELD_BORDER } from '../clinical-notes-section/recordCardStyles';

// Presentational list for the pedigree sidebar EXPORT section.
//
// The section used to be a flat FormGroup of six icon-button + label rows, which
// packed the formats tightly together and gave no clue what any of them were
// for. This renders them instead as grouped cards — one card per purpose
// (images, data exchange, risk tools) — with a full-width hit target, a quiet
// grey icon chip that turns accent on hover, a one-line description and the
// file extension the download actually produces.
//
// Purely presentational: every format, its copy and its handler are declared by
// the caller (ExportMenu), so adding a format later is one entry in that config
// and no change here.

// Local greys. These match the sidebar refresh palette in SidebarSection.jsx
// (mid-slate icon, navy label) — there is no theme token for either.
const CAPTION = '#8994a1';
const LABEL = '#2b3648';
const DESCRIPTION = '#7c8894';
const CHIP_REST_BG = '#f1f4f7';

export default function ExportOptionList({ groups }) {
  const { theme } = React.useContext(ThemeContext) || {};
  const accent = theme?.buttonStyle || theme?.accent || '#2ba9bf';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {groups.map((group) => (
        <Box key={group.id}>
          <Typography
            component="h3"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: CAPTION,
              mb: 0.75,
              pl: 0.25
            }}
          >
            {group.caption}
          </Typography>
          <Box
            sx={{
              backgroundColor: '#fff',
              border: `1px solid ${FIELD_BORDER}`,
              borderRadius: 1.5,
              overflow: 'hidden'
            }}
          >
            {group.options.map((option, index) => (
              <ButtonBase
                key={option.id}
                onClick={option.onClick}
                disabled={option.disabled}
                aria-label={`Export ${option.label}${
                  option.description ? ` — ${option.description}` : ''
                }`}
                {...(option.anchor ? { 'data-va-anchor': option.anchor } : {})}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.25,
                  py: 1.1,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  borderTop:
                    index === 0 ? 'none' : `1px solid ${alpha(LABEL, 0.07)}`,
                  transition: 'background-color .15s ease',
                  '&:hover': {
                    backgroundColor: alpha(accent, 0.06),
                    '& .export-option-chip': {
                      backgroundColor: alpha(accent, 0.14),
                      color: accent
                    },
                    '& .export-option-ext': { color: accent }
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: alpha(accent, 0.06),
                    outline: `2px solid ${alpha(accent, 0.5)}`,
                    outlineOffset: '-2px'
                  },
                  '&.Mui-disabled': { opacity: 0.45 }
                }}
              >
                <Box
                  className="export-option-chip"
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: CHIP_REST_BG,
                    color: CAPTION,
                    transition: 'background-color .15s ease, color .15s ease',
                    '& .MuiSvgIcon-root': { fontSize: 19 }
                  }}
                >
                  {option.icon}
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: LABEL
                    }}
                  >
                    {option.label}
                  </Typography>
                  {option.description && (
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        lineHeight: 1.35,
                        color: DESCRIPTION,
                        mt: '2px'
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
                {option.ext && (
                  <Typography
                    className="export-option-ext"
                    sx={{
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      color: '#9aa5b1',
                      transition: 'color .15s ease'
                    }}
                  >
                    {option.ext}
                  </Typography>
                )}
              </ButtonBase>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
